/**
 * 휴일근무일지 — 작성·보기 모달 (v2.97.0 · 2026-08-26 · 사용자 요청)
 *
 * 양식: `upload/양식/휴일근무일지 양식.xlsx` 「1일 근무용」
 *   근무시작·종료(날짜+시각, 근무시간 자동) / 근무자 최대 4명 / 근무사유 / 근무내용 / 비고
 *
 * ⭐ 작성자 본인은 **자동으로 1번 근무자**다 — 화면에 회색으로 고정해 두고 못 빼게 한다.
 *   (사용자 요청: "작성자 본인 정보는 자동으로 들어가게")
 * ⭐ 추가 근무자로 넣은 사람은 **그 문서를 같이 본다** — 화면에 그렇게 적어 둔다.
 *   말해 주지 않으면 남의 이름을 무심코 넣는다.
 *
 * 결재(승인·반려·회수)는 지출결재와 **같은 API**를 쓴다 — 여기서 다시 만들지 않는다.
 */

import { todayKst } from '../lib/date-label';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../lib/expense-api';
import * as holidayApi from '../lib/holiday-api';
import { EXPENSE_STATUS_BADGE } from './ExpenseDocModal';
import { signatureImageUrl } from '../lib/approval-api';
import { DocHistoryList } from './DocHistoryList';
import { openServerPdfPreview } from '../lib/pdf-preview';
import { useAuth } from '../lib/auth-context';

const MAX_WORKERS = 4;

interface Props {
  /** null = 새로 작성 */
  docId: number | null;
  onClose: () => void;
}

const todayStr = (): string => todayKst();

function emptyForm() {
  return {
    startDate: todayStr(),
    startTime: '09:00',
    endDate: todayStr(),
    endTime: '18:00',
    workReason: '',
    workContent: '',
    remark: '',
    extraWorkerIds: [] as string[],
  };
}

export function HolidayWorkModal({ docId, onClose }: Props) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [msg, setMsg] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ['expense', 'doc', docId],
    queryFn: () => api.getExpenseDoc(docId!),
    enabled: docId !== null,
  });

  const users = useQuery({
    queryKey: ['expense', 'users'],
    queryFn: api.listPortalUsers,
    staleTime: 300_000,
  });

  const doc = detail.data?.doc ?? null;
  const h = detail.data?.holiday ?? null;
  const canEdit = docId === null || (detail.data?.canEdit ?? false);

  // 기존 문서를 열면 그 값으로 폼을 채운다.
  // ⚠deps 에 객체를 넣지 않는다 — 값으로만 넣어야 매 렌더마다 다시 돌지 않는다.
  useEffect(() => {
    if (!h) return;
    setForm({
      startDate: h.startDate,
      startTime: h.startTime,
      endDate: h.endDate,
      endTime: h.endTime,
      workReason: h.workReason,
      workContent: h.workContent,
      remark: h.remark,
      extraWorkerIds: h.workers.filter((w) => w.seq > 1 && w.userId).map((w) => w.userId!),
    });
  }, [h?.startDate, h?.startTime, h?.endDate, h?.endTime, h?.workReason, h?.workContent, h?.remark,
      h?.workers.map((w) => w.userId).join(',')]);

  const durText = holidayApi.localWorkTimeText(
    form.startDate, form.startTime, form.endDate, form.endTime,
  );

  /**
   * 1번 근무자(=작성자)를 뺀 직원 목록 — 그 사람은 이미 1번 칸이라 고를 필요가 없다.
   * ⚠**「나」를 빼면 안 된다** (v2.102.1 실측): 남이 쓴 문서를 근무자가 열면 자기 이름이
   *   목록에서 사라져, 자기가 들어가 있는 칸이 「— 없음 —」으로 보인다.
   *   새로 쓸 때는 작성자가 곧 나라서 지금까지와 결과가 같다.
   */
  const authorUserId = h?.workers?.[0]?.userId ?? user?.userId ?? '';
  const pickable = useMemo(
    () => (users.data ?? []).filter((u) => u.userId !== authorUserId),
    [users.data, authorUserId],
  );

  const save = useMutation({
    mutationFn: (submit: boolean) => {
      const input: holidayApi.HolidaySaveInput = { ...form, submit };
      return docId === null
        ? holidayApi.createHolidayDoc(input)
        : holidayApi.updateHolidayDoc(docId, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expense'] });
      onClose();
    },
    onError: (e) => setMsg((e as Error).message),
  });

  const act = useMutation({
    mutationFn: (kind: 'approve' | 'reject' | 'recall') => {
      if (kind === 'approve') return api.approveExpenseDoc(docId!);
      if (kind === 'recall') {
        // ⭐완결 문서 회수는 사유 필수 (v2.101.0) — 지출결재 모달과 같은 규칙
        if (doc?.status === '최종승인') {
          const r = window.prompt('완결(최종승인) 문서를 회수합니다.\n사유를 적어 주세요 (필수 — 처리 이력에 남습니다)') ?? '';
          if (!r.trim()) return Promise.reject(new Error('사유가 없으면 완결 문서를 회수할 수 없습니다'));
          return api.recallExpenseDoc(docId!, r.trim());
        }
        return api.recallExpenseDoc(docId!);
      }
      const reason = window.prompt('반려 사유를 적어 주세요 (선택)') ?? '';
      return api.rejectExpenseDoc(docId!, reason);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expense'] });
      onClose();
    },
    onError: (e) => window.alert((e as Error).message),
  });

  /**
   * 삭제 — 잘못 쓴 문서를 지울 길이 없으면 사람은 그냥 둔다(그리고 결재함이 지저분해진다).
   * 지울 수 있는 조건은 **서버가 정한다**(canDelete) — 작성자의 작성중·검토대기·반려, 또는 admin.
   */
  const del = useMutation({
    mutationFn: () => api.deleteExpenseDoc(docId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expense'] });
      onClose();
    },
    onError: (e) => window.alert((e as Error).message),
  });

  function setExtra(idx: number, value: string) {
    setForm((f) => {
      const next = [...f.extraWorkerIds];
      if (!value) next.splice(idx, 1);
      else next[idx] = value;
      return { ...f, extraWorkerIds: [...new Set(next.filter(Boolean))] };
    });
  }

  const myName = user?.name ?? user?.userId ?? '';
  const authorRow = docId !== null && h
    ? h.workers.find((w) => w.seq === 1)
    : { seq: 1, userId: user?.userId ?? null, empId: null, name: myName, title: null, department: null };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-[70] flex items-start justify-center overflow-y-auto p-4"
    >
      <div className="bg-white rounded shadow-xl w-full max-w-3xl my-6">
        {/* 머리 */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div className="flex items-center gap-2.5">
            <h2 className="ui-modal-title">휴일근무일지</h2>
            {doc && <span className="text-xs text-slate-500">{doc.docNo}</span>}
            {doc && (
              <span className={`text-xs px-1.5 py-0.5 rounded ${EXPENSE_STATUS_BADGE[doc.status] ?? ''}`}>
                {doc.status}
              </span>
            )}
          </div>
          <button type="button" onClick={onClose} className="ui-close">✕</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {doc?.rejectReason && (
            <div className="text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded px-3 py-2">
              반려 사유: {doc.rejectReason}
            </div>
          )}

          {/* 근무 시각 */}
          <section>
            <div className="text-xs font-semibold text-slate-700 mb-1.5">근무 시각</div>
            {/* ⚠시간 칸 폭 — 한국어 크롬은 「오전 09:00 🕐」로 그려서 **116px** 이 있어야 안 잘린다
                (2026-08-31 실측: text-sm + px-2 기준 자연 폭 116px). 종전 86px 라 「오전 0」으로
                잘려 보였다(사용자 신고). 여유 8px 두고 124px.
                ⭐남는 폭은 전부 날짜 칸(1fr)이 가져간다 — 창이 넓어져도 시간 칸은 안 넓어지므로
                  고정 폭을 자연 폭보다 좁게 두면 화면이 커도 계속 잘린다. */}
            <div className="grid grid-cols-[62px_1fr_124px_auto] gap-2 items-center text-sm">
              <span className="text-slate-500">근무시작</span>
              <input type="date" value={form.startDate} disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value, endDate: f.endDate < e.target.value ? e.target.value : f.endDate }))}
                className="ui-input ui-input-sm" />
              <input type="time" value={form.startTime} disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
                className="ui-input ui-input-sm" />
              <span className="text-slate-500 text-xs">부터</span>

              <span className="text-slate-500">근무종료</span>
              <input type="date" value={form.endDate} disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                className="ui-input ui-input-sm" />
              <input type="time" value={form.endTime} disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))}
                className="ui-input ui-input-sm" />
              <span className="text-slate-500 text-xs">
                까지 {durText && <b className="text-slate-700 ml-1">( {durText} )</b>}
              </span>
            </div>
          </section>

          {/* 근무자 */}
          <section>
            <div className="text-xs font-semibold text-slate-700 mb-1.5">
              근무자 <span className="font-normal text-slate-400">(최대 {MAX_WORKERS}명)</span>
            </div>
            <div className="space-y-1.5 text-sm">
              {/* 1번 = 작성자 본인. 자동으로 들어가고 뺄 수 없다 */}
              <div className="flex items-center gap-2">
                <span className="w-14 text-slate-500 text-xs">근무자 1</span>
                <div className="flex-1 border border-slate-200 bg-slate-50 rounded px-2 py-1 text-slate-700">
                  {authorRow?.name ?? myName}
                  <span className="text-slate-500 text-xs ml-2">
                    {[authorRow?.title, authorRow?.department].filter(Boolean).join(' · ') || '본인'}
                  </span>
                </div>
              </div>
              {Array.from({ length: MAX_WORKERS - 1 }, (_, i) => {
                const sel = form.extraWorkerIds[i] ?? '';
                const chosen = pickable.find((u) => u.userId === sel);
                return (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-14 text-slate-500 text-xs">근무자 {i + 2}</span>
                    <select
                      value={sel}
                      disabled={!canEdit}
                      onChange={(e) => setExtra(i, e.target.value)}
                      className="ui-input ui-input-sm flex-1"
                    >
                      <option value="">— 없음 —</option>
                      {pickable
                        .filter((u) => u.userId === sel || !form.extraWorkerIds.includes(u.userId))
                        .map((u) => (
                          <option key={u.userId} value={u.userId}>
                            {u.name}{u.title ? ` ${u.title}` : ''}{u.department ? ` · ${u.department}` : ''}
                          </option>
                        ))}
                    </select>
                    {chosen && <span className="text-xs text-slate-500 w-24 truncate">{chosen.department ?? ''}</span>}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              직급·부서는 직원 정보에서 자동으로 들어갑니다. ⭐<b>여기에 넣은 분들도 이 문서를 볼 수 있습니다.</b>
            </p>
          </section>

          {/* 근무사유 · 근무내용 · 비고 */}
          <section className="space-y-3">
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-1">근무사유</div>
              <textarea rows={2} value={form.workReason} disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, workReason: e.target.value }))}
                placeholder="예) 노원구청 정전작업 지원"
                className="ui-input ui-textarea w-full" />
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-1">
                근무내용 <span className="font-normal text-slate-400">(한 줄에 하나씩)</span>
              </div>
              <textarea rows={6} value={form.workContent} disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, workContent: e.target.value }))}
                placeholder={'예) 전기작업을 위한 장비 Shutdown\n전기복구 이후 서버·스토리지 서비스 시작\n4월 유지보수 정기점검'}
                className="ui-input ui-textarea w-full" />
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-1">비고</div>
              <textarea rows={2} value={form.remark} disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, remark: e.target.value }))}
                className="ui-input ui-textarea w-full" />
            </div>
          </section>

          {/* 결재판 */}
          {detail.data && detail.data.cells.length > 0 && (
            <section>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">결재</div>
              <div className="flex gap-2">
                {detail.data.cells.map((c) => (
                  <div key={c.role} className="border border-slate-300 rounded text-center w-[86px]">
                    <div className="text-[11px] bg-slate-100 py-0.5 text-slate-600">{c.role}</div>
                    <div className="h-9 flex items-center justify-center text-xs">
                      {c.skipped ? (
                        <span className="text-slate-300">생략</span>
                      ) : c.stamped ? (
                        // ⭐등록된 서명 이미지를 쓴다 — 지출결재 문서 모달과 같은 방식.
                        //   처음엔 이름만 찍었는데, 사용자가 「서명이 텍스트로 들어간다」고 신고했다.
                        //   (PDF 는 처음부터 서명 이미지였다 — 화면만 달랐다.) v2.97.1
                        c.userId && c.hasSignature ? (
                          <img src={signatureImageUrl(c.userId)} alt={c.name}
                            className="max-h-[30px] max-w-[70px] object-contain"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        ) : (
                          <span className="font-semibold text-slate-700">{c.name}</span>
                        )
                      ) : (
                        <span className="text-slate-300">대기</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 pb-0.5">
                      {c.skipped ? '' : (c.stampedAt ?? '').slice(2, 10)}
                      {!c.skipped && c.delegated && (
                        <span className="ml-0.5 text-[11px] text-amber-600 font-semibold">(대결)</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 처리 이력 (v2.99.0) — 반려·회수 경위가 재제출 뒤에도 남는다 */}
          <DocHistoryList history={detail.data?.history} />

          {msg && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{msg}</div>}
        </div>

        {/* 단추 */}
        <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-5 py-3">
          <div className="flex gap-2">
            {detail.data?.attachmentId && (
              <button type="button"
                onClick={() => openServerPdfPreview({
                  pdfUrl: api.attachmentDownloadUrl(detail.data!.attachmentId!),
                  filename: `${doc?.docNo ?? '휴일근무일지'}_휴일근무일지.pdf`,
                })}
                className="btn btn-secondary">
                📄 문서 보기
              </button>
            )}
            {detail.data?.canDelete && (
              <button type="button"
                onClick={() => {
                  if (window.confirm(`${doc?.docNo ?? '이 휴일근무일지'} 를 삭제할까요?`)) del.mutate();
                }}
                disabled={del.isPending}
                className="btn btn-danger">
                {del.isPending ? '삭제 중…' : '🗑 삭제'}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {detail.data?.canRecall && (
              <button type="button" onClick={() => act.mutate('recall')} disabled={act.isPending}
                className="btn btn-secondary">↩ 승인 회수</button>
            )}
            {detail.data?.canReject && (
              <button type="button" onClick={() => act.mutate('reject')} disabled={act.isPending}
                className="btn btn-danger">반려</button>
            )}
            {detail.data?.canApprove && (
              <button type="button" onClick={() => act.mutate('approve')} disabled={act.isPending}
                className="btn btn-success-solid">승인</button>
            )}
            {canEdit && (
              <>
                <button type="button" onClick={() => save.mutate(false)} disabled={save.isPending}
                  className="btn btn-secondary">
                  {save.isPending ? '저장 중…' : '임시저장'}
                </button>
                <button type="button" onClick={() => save.mutate(true)} disabled={save.isPending}
                  className="btn btn-primary">
                  결재 올리기
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
